import React, { useState, useEffect } from 'react';
import { FaShareAlt, FaCopy, FaUsers, FaGift, FaCheck } from 'react-icons/fa';
import { Card, CardContent, Typography, Button, Box, Alert, Snackbar } from '@mui/material';

const ReferralSystem = () => {
  const [referralData, setReferralData] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchReferralData();
    fetchReferralStats();
  }, []);

  const fetchReferralData = async () => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || '') + '/api/user/referral-code', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      } else {
        console.error('Failed to fetch referral data:', response.status);
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
    }
  };

  const fetchReferralStats = async () => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || '') + '/api/user/referral-stats', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReferralStats(data);
      } else {
        console.error('Failed to fetch referral stats:', response.status);
      }
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({
        open: true,
        message: 'تم نسخ الرابط بنجاح!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'فشل في نسخ الرابط',
        severity: 'error'
      });
    }
  };

  const claimReward = async () => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || '') + '/api/user/claim-referral-reward', {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSnackbar({
          open: true,
          message: data.message,
          severity: 'success'
        });
        fetchReferralStats(); // Refresh stats
      } else {
        const error = await response.json();
        setSnackbar({
          open: true,
          message: error.error,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'حدث خطأ في المطالبة بالمكافأة',
        severity: 'error'
      });
    }
  };

  const shareViaWebShare = async () => {
    if (navigator.share && referralData) {
      try {
        await navigator.share({
          title: 'انضم إلى منصة ياعيل ماستر',
          text: 'انضم إلى منصة ياعيل ماستر للتحضير لامتحان ياعيل',
          url: referralData.referralUrl
        });
      } catch (error) {
        // Fallback to copy
        copyToClipboard(referralData.referralUrl);
      }
    } else {
      copyToClipboard(referralData.referralUrl);
    }
  };

  if (loading) {
    return (
      <Card style={{ margin: '20px 0' }}>
        <CardContent>
          <Typography>جاري التحميل...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card style={{ margin: '20px 0', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <FaShareAlt style={{ marginLeft: 8, fontSize: 24 }} />
            <Typography variant="h5" component="h2">
             ادع أصدقاءك
            </Typography>
          </Box>
          
          <Typography variant="body1" style={{ marginBottom: 16 }}>
            ادع أصدقاءك واحصل على أسبوعين اشتراك مجاناً!
          </Typography>

          {referralData && (
            <Box>
              <Typography variant="h6" style={{ marginBottom: 8 }}>
                رابط الدعوة الخاص بك:
              </Typography>
              
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Box 
                  style={{ 
                    flex: 1, 
                    padding: '8px 12px', 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    borderRadius: 8,
                    fontFamily: 'monospace',
                    fontSize: 14,
                    wordBreak: 'break-all'
                  }}
                >
                  {referralData.referralUrl}
                </Box>
                <Button
                  variant="contained"
                  startIcon={<FaCopy />}
                  onClick={() => copyToClipboard(referralData.referralUrl)}
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                >&nbsp;
                  نسخ
                </Button>
              </Box>

              <Button
                variant="contained"
                startIcon={<FaShareAlt />}
                onClick={shareViaWebShare}
                style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', marginBottom: 16 }}
              >&nbsp;
                مشاركة
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {referralStats && (
        <Card style={{ margin: '20px 0' }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <FaUsers style={{ marginLeft: 8, fontSize: 24, color: '#667eea' }} />
              <Typography variant="h6" component="h3">
                إحصائيّات الدعوات
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body1">
                عدد الأصدقاء الذين انضموا:
              </Typography>
              <Typography variant="h6" style={{ color: '#667eea' }}>
                {referralStats.totalReferrals} / 3
              </Typography>
            </Box>

            {referralStats.totalReferrals > 0 && (
              <Box mb={2}>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  الأصدقاء الذين انضموا:
                </Typography>
                {referralStats.referredUsers.map((user, index) => (
                  <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">
                      {user.displayName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(user.signedUpAt).toLocaleDateString('en-GB')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {referralStats.eligibleForReward && !referralStats.rewardClaimed && (
              <Alert 
                severity="success" 
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    startIcon={<FaGift />}
                    onClick={claimReward}
                  >&nbsp;
                    المطالبة بالمكافأة
                  </Button>
                }
                style={{ marginTop: 16 }}
              >
                🎉 تهانينا! لقد حققت 3 دعوات. يمكنك الآن المطالبة بـ أسبوعين اشتراك مجاناً!
              </Alert>
            )}

            {referralStats.rewardClaimed && (
              <Alert severity="info" style={{ marginTop: 16 }}>
                <Box display="flex" alignItems="center">
                  <FaCheck style={{ marginLeft: 8 }} />
                  تم المطالبة بالمكافأة بنجاح!
                </Box>
              </Alert>
            )}

            {referralStats.totalReferrals < 3 && (
              <Alert severity="info" style={{ marginTop: 16 }}>
                تحتاج إلى {3 - referralStats.totalReferrals} دعوات أخرى للحصول على المكافأة!
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default ReferralSystem;
